import { AddToCartButtonType } from '@commerce-cart-webcomponents/ui-composite';
import {
  DerivedAvailabilityStatusInternal,
  ProductType,
  TagType,
  UserSegment,
  checkForPartialBackOrder,
  type IProduct,
  type ProductData,
  type Slot,
} from '@commerce-product-webcomponents/sdk';
import {
  ButtonSize,
  Tag,
  TagStyle,
  Theme,
  UserType,
  useNextgenRouter,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { ConfigService } from '@nextgen-web-framework/core';
import { FC, useEffect, useState } from 'react';
import { isSopQueryParamAvailable } from '../../utils';
import { ProductLink } from '../Hoc';
import {
  AddToWishlistContainer,
  BasicDetailsComponent,
  ConditionalButtonComponent,
  ConditionalPriceRangeComponent,
  ConditionalRetailPriceRangeComponent,
  DerivedAvailabilityStatusComponentGroupKey,
  DetailsType,
  DropdownButton,
  IdAndSizeType,
  ImageComponent,
  ImageType,
  PVBVComponent,
  PVBVType,
  PriceType,
  ProductIdAndSizeComponent,
  StockStatusComponent,
  StockStatusComponentType,
  VariantDrawer,
  includesDerivedAvailabilityStockStatus,
} from '../Molecules';
import { getDesignatedOptionMap } from '../Organisms/Variant/VariantUtils';
import styles from './SearchCard.module.scss';
import { UserTypeEnums } from './types/enums';
import type { SearchCardPropsTypes, BundleSlotEntriesType, BundleSlotType } from './types/propsTypes';

const SearchCard: FC<SearchCardPropsTypes> = (props: SearchCardPropsTypes): JSX.Element => {
  const {
    disabled,
    entryObj,
    className = '',
    size = ButtonSize.SMALL,
    showProductIdAndSizeComp = true,
    showVariantComp = true,
    showAddToCartButtonComp = true,
    showAddToWishlistComp = false,
    productData,
    showSubscriptionSelectButtonComp = false,
    onSubscriptionSelectButtonCB,
    subscriptionVariantActions,
    isMinProjection = true,
    showZeroAsFree = false,
    isFlexibleWidthCard = false,
  } = props;
  const {
    mainLink,
    alt,
    brandName,
    productName,
    productPrice,
    currency,
    productSize,
    pointValue,
    businessVolume,
    variantData,
    productId,
    derivedAvailabilityStatus,
    firstAvailableFutureDate,
    isBundle,
    bundle,
    minPrice,
    maxPrice,
    productType,
    variantGroupId,
    productFlags = {},
    productTags: { label = '', type = '', userTypes = [] } = {},
    productRetailPrice,
    minRetailPrice,
    maxRetailPrice,
  } = productData;
  const t = useTranslation();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [newBundleDerivedStatus, setNewBundleDerivedStatus] = useState<DerivedAvailabilityStatusInternal>(
    derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
  );
  const designatedOptionMap = getDesignatedOptionMap(productId, variantData);
  const [isDisabled, setIsDisabled] = useState<boolean>(disabled as boolean);

  const showLink = productFlags?.restrictFromPdpDisplay ? false : true;
  const { session, isAnonymous, isABO } = useSession();
  const [isUserTypeMatched, setIsUserTypeMatched] = useState<boolean>(false);

  const configs = ConfigService.getConfigs();
  const isProductSOPFeatureAvailable = configs.NEXT_PRODUCT_SOP_FEATURE as boolean;

  const router = useNextgenRouter();
  const isSop = isSopQueryParamAvailable(router.query?.get('sop') || '') && isProductSOPFeatureAvailable;

  useEffect(() => {
    let userTypeMatchFlag = true;
    if (userTypes?.length > 0) {
      let userType = '';
      if (isAnonymous()) {
        userType = UserType.REGISTERED;
      } else if (isABO()) {
        userType = UserTypeEnums.ABO;
      } else {
        userType = session?.type || '';
      }
      userTypeMatchFlag = userTypes.includes(userType as UserSegment);
    }

    setIsUserTypeMatched(userTypeMatchFlag);
  }, [session?.type]);

  const productLinkProps = {
    href: '',
    showLink: showLink,
    productId,
    productName,
    variantGroupId,
    isSop,
  };

  useEffect(() => {
    const prodData = { bundle, derivedAvailabilityStatus, isBundle };
    setNewBundleDerivedStatus(checkForPartialBackOrder(prodData as IProduct));
  }, []);

  useEffect(() => {
    if (
      !showSubscriptionSelectButtonComp &&
      includesDerivedAvailabilityStockStatus(
        derivedAvailabilityStatus,
        DerivedAvailabilityStatusComponentGroupKey.CTA_DISABLED_STATUSES,
      )
    ) {
      setIsDisabled(true);
    }
  }, [derivedAvailabilityStatus, showSubscriptionSelectButtonComp]);

  const formattedVariantName = designatedOptionMap?.optionChoice ?? '';
  const bundleSlotsState: BundleSlotType[] = bundle?.slots?.map((slot: Slot) => {
    const bundleSlot: BundleSlotType = {
      id: slot.id?.toString() as string,
      entries: slot.products?.map((product: ProductData) => {
        const bundleSlotEntry = {
          product: {
            id: product.items[0].itemNumber as string,
          },
          quantity: slot.minQty,
        };
        return bundleSlotEntry;
      }) as unknown as BundleSlotEntriesType[],
    };
    return bundleSlot;
  }) as BundleSlotType[];

  const getAddToCartConfig = () => {
    if (subscriptionVariantActions) {
      return { variantGroup: variantData };
    } else {
      return {
        addToCartConfig: {
          className,
          entryObj,
          productData,
        },
      };
    }
  };
  const bundleCase = isBundle ? t('product.common.bundle') : '';

  const calculateTheme = (type: string) => {
    switch (type) {
      case TagType.Bundle:
      case TagType.Subscription:
        return Theme.BLUE;

      default:
        return Theme.PURPLE;
    }
  };

  const getTagLabel = (entity: string, value: string) => (isUserTypeMatched && entity ? entity : value);

  return (
    <>
      {openDrawer && (
        <VariantDrawer
          link={mainLink}
          alt={alt}
          brandName={brandName}
          productName={productName}
          productPrice={productPrice}
          currency={currency}
          productId={productId}
          productSize={productSize}
          pointValue={pointValue}
          businessVolume={businessVolume}
          onClose={() => {
            setOpenDrawer(false);
          }}
          derivedAvailabilityStatus={derivedAvailabilityStatus}
          {...getAddToCartConfig()}
          subscriptionVariantActions={subscriptionVariantActions}
          showZeroAsFree={showZeroAsFree}
        />
      )}
      <div
        data-testid="search-container-test-id"
        className={`${styles['cpw-search-card-container']} cpw-search-card-container-override ${className} ${
          isFlexibleWidthCard && styles['flexible-card-width']
        }`}
      >
        {showAddToWishlistComp && (
          <div className={styles['heart-icon-container']}>
            <AddToWishlistContainer
              className={styles['heart-icon']}
              isBundle={isBundle ?? false}
              isPLPCard={true}
              productData={productData}
              isPDP={false}
            />
          </div>
        )}
        {getTagLabel(label, bundleCase) && (
          <div className={styles['cpw-tag-container']}>
            <Tag
              className={styles['cpw-tag']}
              text={getTagLabel(label, bundleCase)}
              style={TagStyle.OUTLINE}
              theme={calculateTheme(getTagLabel(type, bundleCase.toLocaleUpperCase()))}
            />
          </div>
        )}
        <div className={styles['card-image-container']}>
          <div className={styles['card-image']}>
            <ProductLink {...productLinkProps}>
              <span data-testid="search-image-test-id" role="button" tabIndex={0} aria-label={alt}>
                <ImageComponent link={mainLink} alt={alt} imagetype={ImageType.PLP_SEARCH_CARD} />
              </span>
            </ProductLink>
          </div>
        </div>
        <div className={styles['card-details']}>
          <div className={styles['cpw-card-basic-details']}>
            <span
              className={styles['card-description']}
              data-testid="search-details-test-id"
              role="button"
              tabIndex={0}
              aria-label={productName}
            >
              <ProductLink {...productLinkProps}>
                <BasicDetailsComponent
                  brandName={brandName}
                  productName={productName}
                  detailsType={DetailsType.PLP_SEARCH_CARD}
                />
              </ProductLink>
              {showProductIdAndSizeComp && (
                <ProductIdAndSizeComponent
                  productId={`${productId}`}
                  productSize={productSize}
                  IdSizeType={IdAndSizeType.IDSIZESEARCHCARD}
                  prefixText={t('product.common.item')}
                />
              )}
            </span>
            <div className={styles['cpw-search-card-price-section']}>
              <ConditionalPriceRangeComponent
                isBundle={isBundle}
                minPrice={minPrice as number}
                maxPrice={maxPrice as number}
                currency={currency}
                productPrice={productPrice}
                condionalPriceType={PriceType.PRICE_PLP_SEARCH_CARD}
                showZeroAsFree={showZeroAsFree}
              />
              <ConditionalRetailPriceRangeComponent
                isBundle={isBundle}
                minPrice={minRetailPrice as number}
                maxPrice={maxRetailPrice as number}
                currency={currency}
                productPrice={productRetailPrice as number}
                condionalPriceType={PriceType.PRICE_PLP_SEARCH_CARD}
                isRetailPrice={true}
              />
              <PVBVComponent
                pointValue={pointValue}
                businessVolume={businessVolume}
                pvbvType={PVBVType.PVBVSEARCHCARD}
              />
            </div>
            <StockStatusComponent
              derivedAvailabilityStatus={newBundleDerivedStatus}
              showSubText={true}
              stockStatusType={StockStatusComponentType.STOCK_STATUS_SEARCH_CARD}
              firstAvailableFutureDate={firstAvailableFutureDate}
            />
          </div>
          <div className={styles['cpw-search-card-cta-section']}>
            {showVariantComp && variantData && !isBundle && productType === ProductType.Variant && (
              <DropdownButton
                label={formattedVariantName}
                dropdownEventHandler={() => setOpenDrawer(true)}
                className={styles['cpw-search-card-dropdown-button']}
                disabled={
                  disabled ||
                  ([DerivedAvailabilityStatusInternal.Nya, DerivedAvailabilityStatusInternal.Naw] as string[]).includes(
                    derivedAvailabilityStatus || '',
                  )
                }
              />
            )}
            {showAddToCartButtonComp && (
              <ConditionalButtonComponent
                disabled={isDisabled}
                derivedAvailabilityStatus={derivedAvailabilityStatus}
                className={styles['search-card-btn']}
                entryObj={entryObj}
                bundleSlots={bundleSlotsState}
                title={t('product.common.addtoCart')}
                size={size}
                addToCartButtonType={AddToCartButtonType?.ADD_TO_CART_BUTTON}
                productData={productData}
                showSubscriptionSelectButtonComp={showSubscriptionSelectButtonComp}
                onSubscriptionSelectButtonCB={onSubscriptionSelectButtonCB}
                isMinProjection={isMinProjection}
                isSop={isSop}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchCard;
export type { SearchCardPropsTypes, BundleSlotEntriesType, BundleSlotType };
